import { Inbox, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { cn } from '@/lib/utils'

/**
 * The shared empty state (PX-W6.2) — a lucide glyph in a muted circle, a
 * heading, a body, and an optional single action. `role="status"` so the
 * absence is announced, never a silent blank (INV-9).
 *
 * PX-W6.6 stamps `data-triad="empty"` on the root (see `LoadingPanel`).
 */
export function EmptyPanel({
  title,
  body,
  icon: Icon = Inbox,
  actionLabel,
  onActionClicked,
  className
}: {
  title?: string
  body?: string
  /** A lucide glyph standing in for an illustration. */
  icon?: LucideIcon
  actionLabel?: string
  onActionClicked?: () => void
  className?: string
}) {
  const { t } = useTranslation('common')
  return (
    <div
      role="status"
      data-triad="empty"
      className={cn('rounded-lg border border-border bg-card px-6 py-12 text-center', className)}
    >
      <span
        aria-hidden
        className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground"
      >
        <Icon className="h-5 w-5" />
      </span>
      <SectionLabel className="mt-3">{title ?? t('states.emptyTitle')}</SectionLabel>
      <p className="mx-auto mt-1.5 max-w-md text-sm text-ink-soft">
        {body ?? t('states.emptyBody')}
      </p>
      {actionLabel !== undefined && onActionClicked !== undefined && (
        <button
          type="button"
          onClick={onActionClicked}
          className="mt-4 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {actionLabel}
        </button>
      )}
    </div>
  )
}
